import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { safeJsonParsing } from '../../../common/safeJsonParsing'
import {
  PropertyNoteFilter,
  usePropertyNoteTableQuery,
} from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { ColorPalleteKey } from '../../utils/collorPallete'
import { stringSorter, utcDateSorter } from '../../utils/generalTableHelpers'
import { IconPalleteKey } from '../../utils/iconPallete'
import { sparsePagination } from '../../utils/layoutConst'
import {
  PropertyDetailNote,
  PropertyNoteLink,
} from '../../utils/propertyDetailNoteHelpers'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useTableLocale } from '../../utils/useTableLocale'
import { Min } from '../DateFormat/DateFormat'
import { NoteIcon } from '../NoteIcon/NoteIcon'
import { PropertyDetailLink } from '../PropertyDetailLink/PropertyDetailLink'
import { PropertyDetailNoteDeleteButton } from '../PropertyDetailNoteDeleteButton/PropertyDetailNoteDeleteButton'
import { PropertyDetailNoteEditButton } from '../PropertyDetailNoteEditButton/PropertyDetailNoteEditButton'
import { PropertyDetailNoteLinks } from '../PropertyDetailNoteLinks/PropertyDetailNoteLinks'
import { TableSummaryExport } from '../TableSummaryExport/TableSummaryExport'

export interface PropertyNoteTableProps {
  noteFilter?: PropertyNoteFilter
}

const tableId = 'propertyNote-table'
const scrollX = { x: 1200 }

const exportData = (dataSource: PropertyDetailNote[]) => {
  return dataSource.map(item => ({
    ...item,
    links: item.links && safeJsonParsing<PropertyNoteLink[]>(item.links, []),
  }))
}

export const PropertyNoteTable: FunctionComponent<PropertyNoteTableProps> =
  () => {
    const { tenantId, userId } = useAppSelector(state => state.myProfile)
    const { searchFilter, propertyTypeFilter, dateFilter } =
      useColumnFilters<PropertyDetailNote>()

    const [queryResult, refetch] = usePropertyNoteTableQuery({
      variables: { accountId: tenantId },
    })

    const dataSource = useMemo(
      () =>
        queryResult?.data?.allPropertyNotes?.nodes?.length
          ? (queryResult.data.allPropertyNotes.nodes as PropertyDetailNote[])
          : [],
      [queryResult.data?.allPropertyNotes?.nodes]
    )

    const columns = useMemo<ColumnsType<PropertyDetailNote>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Vytvořeno',
          dataIndex: 'createdAt',
          className: 'w-32',
          ...dateFilter('createdAt'),
          sorter: utcDateSorter('createdAt'),
          render: createdAt => <Min>{createdAt}</Min>,
        },
        {
          title: 'Typ',
          dataIndex: 'propertyType',
          className: 'first-letter:uppercase',
          width: 84,
          sorter: stringSorter('propertyType'),
          ...propertyTypeFilter('propertyType'),
        },
        {
          title: 'Nemovitost',
          dataIndex: 'propertyName',
          className: 'first-letter:uppercase 2xl:w-96 lg:w-72 md:w-52',
          ellipsis: true,
          sorter: stringSorter('propertyName'),
          ...searchFilter('propertyName', 'Hledat nemovitost'),
          render: (propertyName, record) => (
            <PropertyDetailLink
              propertyId={record.propertyId}
              propertyType={record.propertyType as PROPERTY_TYPES}
              drawerTarget
              text={propertyName}
            />
          ),
        },
        {
          title: 'Ikona',
          dataIndex: 'icon',
          className: 'w-16 !pr-0 !py-0 !border-r-0',
          sorter: stringSorter('iconKey'),
          render: (_, record) => (
            <div className="flex justify-center scale-75">
              <NoteIcon
                colorKey={record.colorKey as ColorPalleteKey}
                iconKey={record.iconKey as IconPalleteKey}
              />
            </div>
          ),
        },
        {
          title: 'Poznámka',
          dataIndex: 'note',
          sorter: stringSorter('note'),
          ...searchFilter('note', 'Hledat v textu poznámky'),
          render: (_, record) => (
            <div>
              {record.note}
              <PropertyDetailNoteLinks links={record.links} />
            </div>
          ),
        },
        {
          title: 'Akce',
          dataIndex: 'actions',
          key: 'actions',
          className: 'w-40 !text-right',
          render: (_, record) =>
            userId === record.accountUserId ? (
              <div className="flex space-x-2 text-right">
                <PropertyDetailNoteEditButton note={record} refetch={refetch} />
                <PropertyDetailNoteDeleteButton
                  noteId={record.id}
                  refetch={refetch}
                />
              </div>
            ) : null,
        },
      ],
      [dateFilter, propertyTypeFilter, searchFilter, refetch, userId]
    )

    const summary = useCallback(
      () => (
        <TableSummaryExport
          total={dataSource?.length || 0}
          colSpan={columns.length - 2}
          url="propertyNote/noteXlsx"
          fileName="Poznamky"
          dataSource={exportData(dataSource) || []}
        />
      ),
      [columns.length, dataSource]
    )

    return (
      <Table
        id={tableId}
        rowKey="id"
        size="small"
        rowClassName="animate-fadeIn"
        bordered
        scroll={scrollX}
        dataSource={dataSource}
        columns={columns}
        loading={queryResult.fetching}
        summary={summary}
        locale={useTableLocale(queryResult.fetching)}
        pagination={sparsePagination}
      />
    )
  }
